import { useState, useRef } from 'react';
import { importApi } from '../lib/api';
import Button from '../components/Button';
import Card from '../components/Card';
import Skeleton from '../components/Skeleton';

export default function Import() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dataType, setDataType] = useState<'cells' | 'packs' | 'tests'>('cells');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    total: number;
    batches: number;
    duplicatesRemoved?: number;
    successCount: number;
    failedCount: number;
    failedItems?: Array<{ key: string; error: string }>;
    results: Array<{ success: boolean; count: number; error?: string }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ShimmerLoader = () => (
    <div className="space-y-3">
      <Skeleton className="h-4 w-1/3 mx-auto" />
      <Skeleton className="h-2 w-full" />
    </div>
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setError(null);
        setResult(null);
      } else {
        setError('Please select an Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const response = await importApi.upload(file, dataType);
      setResult(response);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setDataType('cells');
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const successCount = result?.successCount ?? 0;
  const failCount = result?.failedCount ?? 0;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 leading-tight">
          Import Excel Data
        </h1>
        <p className="text-sm md:text-lg text-gray-500 font-medium mt-1">
          Upload Excel files to import cells, packs, and test data
        </p>
      </div>

      <Card>
        <div className="space-y-6">
          <div>
            <label className="block text-base font-semibold text-gray-700 mb-3">
              Data Type
            </label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value as 'cells' | 'packs' | 'tests')}
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="cells">Cells</option>
              <option value="packs">Packs</option>
              <option value="tests">Tests</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Select the type of data you are importing from the Excel file
            </p>
          </div>

          <div>
            <label className="block text-base font-semibold text-gray-700 mb-3">
              Select Excel File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="file-input"
              disabled={uploading}
            />
            <label
              htmlFor="file-input"
              className={`touch-target w-full flex items-center justify-center px-6 py-12 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${uploading
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : file
                    ? 'border-blue-300 bg-blue-50/30 hover:border-blue-400 hover:bg-blue-50/50'
                    : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30'
                }`}
            >
              <div className="text-center">
                <svg
                  className={`mx-auto h-16 w-16 mb-4 transition-colors ${file ? 'text-blue-500' : 'text-gray-400'
                    }`}
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="mt-2 block text-base font-semibold text-gray-900">
                  {file ? file.name : 'Click to select file'}
                </span>
                <span className="mt-2 block text-sm text-gray-500">
                  Excel files only (.xlsx, .xls)
                </span>
              </div>
            </label>
          </div>

          {file && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClear}
                  disabled={uploading}
                  className="text-gray-400 hover:text-red-600 touch-target min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <ShimmerLoader />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-1">Error</h4>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-900 mb-3">
                      Import Complete
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Total Items</p>
                        <p className="text-2xl font-bold text-gray-900">{result.total}</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Batches</p>
                        <p className="text-2xl font-bold text-gray-900">{result.batches}</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Success</p>
                        <p className="text-2xl font-bold text-green-700">{successCount}</p>
                      </div>
                      {result.duplicatesRemoved ? (
                        <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Duplicates Skipped</p>
                          <p className="text-2xl font-bold text-amber-700">{result.duplicatesRemoved}</p>
                        </div>
                      ) : (
                        <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Failed</p>
                          <p className={`text-2xl font-bold ${failCount > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                            {failCount}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {result.failedItems && result.failedItems.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-900 mb-2">
                        Failed Items ({result.failedItems.length} shown)
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                        {result.failedItems.map((item, index) => (
                          <div key={index} className="bg-white rounded-lg p-3 border border-red-100 shadow-sm">
                            <p className="text-xs font-mono text-gray-800 break-all mb-1">{item.key}</p>
                            <p className="text-xs text-red-700 font-medium">{item.error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4 pt-4 border-t border-gray-200">
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              fullWidth
              className="md:w-auto"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Uploading...
                </span>
              ) : (
                `Upload & Import ${dataType.charAt(0).toUpperCase() + dataType.slice(1)}`
              )}
            </Button>
            {file && (
              <Button
                variant="secondary"
                onClick={handleClear}
                disabled={uploading}
                fullWidth
                className="md:w-auto"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}


